/**
 * DraftContractsPanel — S31. Account Management view of Won-deal draft
 * contracts auto-created when a pipeline lead moves to Won. The assigned
 * Account Manager sets the service start date, adjusts terms and finalizes.
 */
import { useCallback, useEffect, useState } from 'react';
import { FileSignature, RefreshCw, CheckCircle2 } from 'lucide-react';
import { apiFetch } from '../../lib/apiFetch';
import { toast } from 'sonner';

const GOLD = '#C9A962';
const inputCls = 'rounded bg-zinc-800 border border-zinc-600 px-2 py-1 text-white text-xs';

function DraftRow({ d, onSaved }) {
  const [startDate, setStartDate] = useState(d.service_start_date || '');
  const [notes, setNotes] = useState(d.notes || '');
  const [busy, setBusy] = useState(false);
  const final = d.status === 'finalized';

  const save = async (finalize) => {
    if (finalize && !startDate) { toast.error('Set a service start date before finalizing'); return; }
    setBusy(true);
    try {
      const body = { service_start_date: startDate || undefined, notes: notes || undefined };
      if (finalize) body.status = 'finalized';
      await apiFetch(`/api/service-packages/draft-contracts/${d.draft_contract_id}`, { method: 'PATCH', body });
      toast.success(finalize ? 'Contract finalized' : 'Draft saved');
      onSaved();
    } catch (e) { toast.error(e.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className={`rounded-lg border p-3 ${final ? 'border-emerald-700 bg-emerald-950/20' : 'border-zinc-700 bg-zinc-800/40'}`}
      data-testid={`draft-contract-${d.draft_contract_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-bold text-white">{d.property_name || d.company_name}
            <span className="ml-2 px-1.5 py-0.5 rounded bg-zinc-800 text-[9px] uppercase tracking-wider font-bold" style={{ color: GOLD }}>{d.business_type}</span>
            {final && <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald-700 text-white text-[9px] font-black uppercase">Finalized</span>}
          </p>
          <p className="text-[11px] text-zinc-400 mt-0.5">
            {d.client_name}{d.company_name ? ` · ${d.company_name}` : ''}{d.email ? ` · ${d.email}` : ''}
          </p>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Package: <span className="text-zinc-300">{d.selected_package || '—'}</span>
            {' · '}Monthly: <span className="text-emerald-400 font-bold">{d.monthly_investment ? `$${Number(d.monthly_investment).toLocaleString()}` : 'pending'}</span>
            {' · '}Term: <span className="text-zinc-300">{d.contract_term_months} mo</span>
            {' · '}AM: <span style={{ color: GOLD }}>{d.account_manager || '—'}</span>
            {Array.isArray(d.onboarding_checklist) && d.onboarding_checklist.length > 0 && (
              <>
                {' · '}Onboarding: <span className={d.onboarding_checklist.every((s) => s.done) ? 'text-emerald-400 font-bold' : 'text-amber-400 font-bold'}
                  data-testid={`dc-onboarding-chip-${d.draft_contract_id}`}>
                  {d.onboarding_checklist.filter((s) => s.done).length}/{d.onboarding_checklist.length}
                </span>
              </>
            )}
          </p>
        </div>
        <p className="text-[10px] text-zinc-600">Won {new Date(d.created_at).toLocaleDateString()}</p>
      </div>
      {!final && (
        <div className="flex flex-wrap items-end gap-2 mt-2">
          <div>
            <p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">Service Start Date</p>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className={inputCls} data-testid={`dc-start-date-${d.draft_contract_id}`} />
          </div>
          <div className="flex-1 min-w-[180px]">
            <p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">Notes</p>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Onboarding notes…"
              className={`${inputCls} w-full`} data-testid={`dc-notes-${d.draft_contract_id}`} />
          </div>
          <button onClick={() => save(false)} disabled={busy}
            className="px-3 py-1.5 rounded-full text-[11px] font-bold border disabled:opacity-50"
            style={{ borderColor: GOLD, color: GOLD }} data-testid={`dc-save-${d.draft_contract_id}`}>
            Save
          </button>
          <button onClick={() => save(true)} disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold disabled:opacity-50"
            style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid={`dc-finalize-${d.draft_contract_id}`}>
            <CheckCircle2 className="h-3 w-3" /> Finalize
          </button>
        </div>
      )}
      {final && (
        <p className="text-[10px] text-emerald-400 mt-1.5">
          Service starts {d.service_start_date} · finalized by {d.finalized_by} {d.notes ? `· ${d.notes}` : ''}
        </p>
      )}
    </div>
  );
}

export default function DraftContractsPanel() {
  const [drafts, setDrafts] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch('/api/service-packages/draft-contracts');
      setDrafts(data.drafts || []);
    } catch (_) { setDrafts([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (drafts === null) return null;

  return (
    <section className="rounded-xl border border-zinc-700 bg-zinc-900/70 p-5" data-testid="draft-contracts-panel">
      <div className="flex items-center justify-between mb-3">
        <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.2em]" style={{ color: GOLD }}>
          <FileSignature className="h-4 w-4" /> Account Management · Won Deal Draft Contracts
        </h2>
        <button onClick={load} className="p-1.5 rounded-full border border-zinc-600 text-zinc-300 hover:text-white" data-testid="dc-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>
      {drafts.length === 0 ? (
        <p className="text-xs text-zinc-500">No won deals yet — move a pipeline lead to Won and its draft contract will appear here automatically.</p>
      ) : (
        <div className="space-y-2">
          {drafts.map((d) => <DraftRow key={d.draft_contract_id} d={d} onSaved={load} />)}
        </div>
      )}
    </section>
  );
}
